import React, { useState, useEffect } from 'react';
import { sendTelegramMessageViaWebhook, getChatId } from '@/services/n8nService';
import { findBestResponse } from '@/data/chatbotKnowledge';
import { Message } from './types';
import ChatbotButton from './ChatbotButton';
import ChatbotPanel from './ChatbotPanel';

const ChatbotContainer: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [useTelegram, setUseTelegram] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([
    {
      text: "Bonjour! Je suis Manon, votre assistante CV Pro Québec. Comment puis-je vous aider aujourd'hui?",
      sender: 'bot',
      timestamp: new Date(),
      platform: 'web'
    },
  ]);

  useEffect(() => {
    setIsLoaded(true);
    const currentChatId = getChatId();
    setChatId(currentChatId);

    document.body.classList.add('chatbot-loaded');

    const styleId = 'chatbot-position-override';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        .chatbot-container {
          position: fixed !important;
          top: auto !important;
          left: auto !important;
          right: 20px !important;
          bottom: 20px !important;
          z-index: 999 !important;
          transform: translateX(0) !important;
        }
      `;
      document.head.appendChild(styleElement);
    }

    console.log('Chatbot loaded with chatId:', currentChatId);

    return () => {
      document.body.classList.remove('chatbot-loaded');
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    if (isMinimized) setIsMinimized(false);
  };

  const handleToggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleToggleTelegram = () => {
    setUseTelegram(!useTelegram);
  };

  const handleSendMessage = async (messageText: string) => {
    const userMessage: Message = {
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
      platform: useTelegram ? 'telegram' : 'web'
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      let responseText: string;

      if (useTelegram) {
        const webhookResponse = await sendTelegramMessageViaWebhook(messageText);
        responseText = webhookResponse?.response || findBestResponse(messageText);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 600));
        responseText = findBestResponse(messageText);
      }

      const botMessage: Message = {
        text: responseText,
        sender: 'bot',
        timestamp: new Date(),
        platform: useTelegram ? 'telegram' : 'web'
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Erreur chatbot:', error);

      const botMessage: Message = {
        text: findBestResponse(messageText),
        sender: 'bot',
        timestamp: new Date(),
        platform: useTelegram ? 'telegram' : 'web'
      };

      setMessages((prev) => [...prev, botMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <div className="chatbot-container">
      {isOpen ? (
        <ChatbotPanel
          isMinimized={isMinimized}
          useTelegram={useTelegram}
          messages={messages}
          onToggleMinimize={handleToggleMinimize}
          onToggleChat={handleToggleChat}
          onToggleTelegram={handleToggleTelegram}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          chatId={chatId}
        />
      ) : (
        <ChatbotButton onClick={handleToggleChat} />
      )}
    </div>
  );
};

export default ChatbotContainer;
